import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Mail, Search } from "lucide-react";
import { cn } from "@/shared/lib/utils";
import { Button } from "@/shared/components/ui/button";
import { InfoHint } from "@/shared/components/ui/info-hint";
import { Card } from "@/shared/components/ui/card";
import { StatusText } from "@/shared/components/ui/status-chip";
import { KpiStrip, KpiTile } from "@/shared/components/ui/kpi";
import { TableCard, TableHeadRow, TableRow, Th, Empty } from "@/shared/components/ui/table";
import { EmptyState } from "@/shared/components/ui/empty-state";
import { useEnrichCampaign } from "../use-enrichment";
import { useBrowseLeads } from "../use-leads";
import { LeadDetailDrawer } from "../leads/LeadDetailDrawer";
import type { Lead } from "../types";

// One page of the campaign's leads is enough at MVP scale (cap 100). If a campaign ever
// exceeds this, the tiles would undercount — revisit with a server-side enrichment rollup.
const PAGE = 100;

type Bucket = "all" | "found" | "noEmail" | "pending" | "notEligible";

const emailOf = (l: Lead) => l.contacts?.find((c) => c.email)?.email ?? null;
const verifiedOf = (l: Lead) => l.contacts?.find((c) => c.email)?.verified ?? null;
const optedOutOf = (l: Lead) => l.contacts?.find((c) => c.email)?.optedOut ?? false;
const companyOf = (l: Lead) =>
  ((l.aiData as { enrichment?: { company?: { name?: string } } } | null)?.enrichment?.company?.name) ?? null;

/** Which enrichment bucket a lead falls into. APPROVED leads split by whether an email
 *  was found / an attempt was made; everything not APPROVED is simply not eligible. */
function bucketOf(l: Lead): Exclude<Bucket, "all"> {
  if (l.status !== "APPROVED") return "notEligible";
  if (emailOf(l)) return "found";
  if (l.enrichedAt != null) return "noEmail";
  return "pending";
}

const TILES: { key: Bucket; label: string }[] = [
  { key: "all", label: "All leads" },
  { key: "found", label: "Email found" },
  { key: "noEmail", label: "No email" },
  { key: "pending", label: "Approved · pending" },
  { key: "notEligible", label: "Not eligible" },
];

/**
 * Enrichment (screens.md §6) — the campaign's leads seen through the enrichment lens.
 * Waterfall enrichment (Hunter → fallback) runs over APPROVED, not-yet-enriched leads
 * and verifies deliverability. Rather than a long stack of sections, the stat tiles ARE
 * the filter: click one to narrow the single list below (segmented-control pattern).
 */
export function EnrichmentTab() {
  const { id: campaignId } = useParams<{ id: string }>();
  const leads = useBrowseLeads({ campaignId: campaignId ?? "", pageSize: PAGE });
  const enrich = useEnrichCampaign(campaignId ?? "");
  const [bucket, setBucket] = useState<Bucket>("all");
  // Open the same deep detail drawer as the Leads screen — it also has Approve/Reject
  // inline, so a not-eligible candidate can be approved right here and then enriched.
  const [openId, setOpenId] = useState<string | null>(null);

  const items = useMemo(() => leads.data?.items ?? [], [leads.data]);
  const counts = useMemo(() => {
    const c = { all: items.length, found: 0, noEmail: 0, pending: 0, notEligible: 0 };
    for (const l of items) c[bucketOf(l)] += 1;
    return c as Record<Bucket, number>;
  }, [items]);

  const rows = useMemo(
    () => (bucket === "all" ? items : items.filter((l) => bucketOf(l) === bucket)),
    [items, bucket],
  );

  const approved = counts.found + counts.noEmail + counts.pending;
  const hitRate = approved > 0 ? Math.round((counts.found / approved) * 100) : null;
  // When the campaign has more leads than one page, the page-derived counts (incl.
  // `pending`) can undercount — so we must NOT let a page-local "pending===0" disable the
  // action (there may be pending leads beyond the page; the server enriches ALL of them).
  const total = leads.data?.total ?? 0;
  const capped = total > PAGE;
  // "Nothing approved yet" is a DISTINCT state from "all approved are enriched": enrichment
  // only runs on APPROVED leads, so if everything is still a candidate we must guide the
  // user to approve first, not tell them it's all done.
  const nothingApproved = !capped && approved === 0;
  const knownAllEnriched = !capped && counts.pending === 0 && approved > 0;
  const runDisabled = enrich.isPending || !campaignId || leads.isLoading || (!capped && counts.pending === 0);
  const runLabel = enrich.isPending
    ? "Enriching…"
    : leads.isLoading
      ? "Loading…"
      : nothingApproved
        ? "No approved leads"
        : knownAllEnriched
          ? "All enriched"
          : capped
            ? "Run enrichment"
            : `Enrich ${counts.pending} pending`;

  return (
    <div className="space-y-6">
      {/* Action header */}
      <Card className="flex flex-wrap items-center gap-3 p-[18px]">
        <Search className="h-[16px] w-[16px] text-ge-accent" strokeWidth={1.8} />
        <div className="min-w-0 flex-1">
          <p className="flex items-center gap-1 text-[14px] font-semibold text-ge-primary">
            Find contact emails
            <InfoHint text="Runs the enrichment waterfall (Hunter → fallback) over approved leads to find emails, phone and company data, and verifies deliverability. Undeliverable is dropped; risky is kept but flagged." />
          </p>
          <p className="text-[12.5px] text-ge-secondary">
            {nothingApproved && counts.notEligible > 0 ? (
              <>
                {counts.notEligible} lead{counts.notEligible === 1 ? "" : "s"} still need approval — open one and
                approve it (or use the Leads tab) before enriching.
              </>
            ) : capped ? (
              <>Runs the waterfall over approved leads that haven&apos;t been enriched yet.</>
            ) : (
              <>
                Runs the waterfall over the {counts.pending} approved lead{counts.pending === 1 ? "" : "s"} still
                pending.
              </>
            )}{" "}
            Undeliverable addresses are dropped; risky ones are kept but flagged.
            {hitRate != null && !capped && <> · {hitRate}% of approved have an email so far.</>}
          </p>
        </div>
        <Button data-testid="run-enrich" disabled={runDisabled} onClick={() => enrich.mutate()}>
          <Mail className="h-[14px] w-[14px]" strokeWidth={2.2} />
          {runLabel}
        </Button>
      </Card>

      {enrich.isError && (
        <p className="text-[12px] text-ge-danger" data-testid="enrich-error">
          Enrichment failed. Make sure there are approved leads and try again.
        </p>
      )}

      {/* Post-run feedback (#2): after a run, say exactly what happened — so a run that
          finds nothing is legible ("0 found · 5 skipped without a website") instead of the
          list silently re-bucketing. */}
      {enrich.data && !enrich.isPending && (
        <div
          data-testid="enrich-summary"
          className="rounded-ge-card border border-ge-border-card bg-ge-card px-4 py-3 text-[12.5px] text-ge-secondary"
        >
          Last run: enriched <span className="font-semibold text-ge-primary">{enrich.data.attempted}</span> lead
          {enrich.data.attempted === 1 ? "" : "s"} · found{" "}
          <span className="font-semibold text-ge-accent">{enrich.data.found}</span> email
          {enrich.data.found === 1 ? "" : "s"}
          {(enrich.data.skippedNoDomain ?? 0) > 0 && (
            <> · {enrich.data.skippedNoDomain} skipped (no website for Hunter to search)</>
          )}
          {enrich.data.attempted === 0 && <> — nothing was approved to enrich.</>}
        </div>
      )}

      {/* Stat tiles = the filter (click to narrow the list) */}
      <KpiStrip>
        {TILES.map((t) => (
          <KpiTile
            key={t.key}
            data-testid={`tile-${t.key}`}
            label={t.label}
            value={String(counts[t.key])}
            selected={bucket === t.key}
            onClick={() => setBucket(t.key)}
          />
        ))}
      </KpiStrip>

      {/* One list, filtered by the active tile */}
      {leads.isLoading ? (
        <p className="font-mono text-[12px] text-ge-muted" data-testid="enrich-loading">
          Loading leads…
        </p>
      ) : items.length === 0 ? (
        <EmptyState message="No leads in this campaign yet. Discover and approve some first, then enrich them here." />
      ) : rows.length === 0 ? (
        <EmptyState message="No leads in this group." />
      ) : (
        <div data-testid="enrich-list">
          <TableCard>
            <TableHeadRow grid="grid-cols-[1.6fr_2.4fr_1.3fr_1.6fr_1.1fr]">
              <Th>Lead</Th>
              <Th>Email</Th>
              <Th>Phone</Th>
              <Th>Company</Th>
              <Th>State</Th>
            </TableHeadRow>
            {rows.map((l) => {
              const b = bucketOf(l);
              const email = emailOf(l);
              const company = companyOf(l);
              return (
                <TableRow
                  key={l.id}
                  grid="grid-cols-[1.6fr_2.4fr_1.3fr_1.6fr_1.1fr]"
                  data-testid={`enrich-row-${l.id}`}
                  role="button"
                  tabIndex={0}
                  onClick={() => setOpenId(l.id)}
                  onKeyDown={(e: React.KeyboardEvent) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setOpenId(l.id);
                    }
                  }}
                  className={cn("cursor-pointer hover:bg-ge-card", b === "notEligible" && "opacity-[.6]")}
                >
                  <div className="min-w-0">
                    <div className="truncate font-sans text-[13.5px] font-semibold text-ge-primary">{l.name}</div>
                    {l.website && <div className="truncate font-mono text-[11px] text-ge-faint">{l.website}</div>}
                  </div>
                  <div className="truncate font-mono text-[11.5px] text-ge-value">{email ?? <Empty />}</div>
                  <div className="truncate font-mono text-[11.5px] text-ge-value">{l.phone ?? <Empty />}</div>
                  <div className="truncate text-[12.5px] text-ge-value">{company ?? <Empty />}</div>
                  <div>
                    <EnrichState bucket={b} verified={verifiedOf(l)} optedOut={optedOutOf(l)} status={l.status} reason={l.screenReason} />
                  </div>
                </TableRow>
              );
            })}
          </TableCard>
        </div>
      )}

      {/* Same deep detail drawer as the Leads screen (approve/reject inline). */}
      <LeadDetailDrawer id={openId} onClose={() => setOpenId(null)} />
    </div>
  );
}

function EnrichState({
  bucket,
  verified,
  optedOut,
  status,
  reason,
}: {
  bucket: Exclude<Bucket, "all">;
  verified: boolean | null;
  optedOut: boolean;
  status: string;
  reason: string | null;
}) {
  if (bucket === "found") {
    // Opted-out is the priority flag: an email was found, but it must NOT be contacted.
    if (optedOut) return <StatusText tone="danger">OPTED OUT</StatusText>;
    if (verified === true) return <StatusText tone="accent">VERIFIED</StatusText>;
    if (verified === false) return <StatusText tone="warning">FLAGGED</StatusText>;
    // Email present but deliverability unknown — don't paint it as risky.
    return <StatusText tone="value">UNVERIFIED</StatusText>;
  }
  if (bucket === "noEmail") return <StatusText tone="faint">NO EMAIL</StatusText>;
  if (bucket === "pending") return <StatusText tone="value">NOT RUN</StatusText>;
  // notEligible: the lead's own status is the reason it's skipped.
  return (
    <span title={reason ?? undefined}>
      <StatusText tone="danger">{status}</StatusText>
    </span>
  );
}
